import { ShoppingCartOutlined } from '@ant-design/icons';
import { FishingReport } from '@omniafishing/core';
import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../../../hooks/use_cart';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useResponsive } from '../../../../hooks/use_responsive';
import { customAttributesToAttributionQueryParams } from '../../../../lib/line_item_attribution';
import { GlobalQueryParams } from '../../../../lib/query_string';
import { setShopifyImgWidth } from '../../../../lib/set_shopify_img_width';
import { toDollars } from '../../../../lib/to_dollars';
import { OmniaUrls } from '../../../../lib/urls';
import { WebAnalytics, WebAnalyticsEventAreas } from '../../../../lib/web_analytics';
import { LineItemCustomAttribute, LINE_ATTRIBUTION } from '../../../../redux/cart';
import { ExtendedMapQueryParams } from '../../../../routes/map_page/lib/map_page_types';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { SeasonIcon } from '../../../season_icon/season_icon';
import styles from './map_hotbait_card.less';

interface MapHotbaitCardProps {
  fishingReport: FishingReport;
  listUuid: string;
  position: number;
  trending?: boolean;
}

export const MapHotbaitCard = (props: MapHotbaitCardProps) => {
  const { fishingReport, listUuid, position, trending } = props;
  const { featured_product, season } = fishingReport;
  const {
    img_url,
    title,
    on_sale,
    in_stock,
    price,
    compare_at_price,
    shopify_product_handle,
    shopify_options,
  } = featured_product;

  const { replaceQueryString } = useQueryString();
  const { addToCart } = useCart();
  const { isMobile } = useResponsive();

  const lineItemCustomAttributes: LineItemCustomAttribute[] = [
    {
      key: LINE_ATTRIBUTION.FISHING_REPORT_ID,
      value: fishingReport.id.toString(),
    },
    {
      key: LINE_ATTRIBUTION.WATERBODY_ID,
      value: fishingReport.waterbody.id,
    },
  ];

  const productUrl = OmniaUrls.product(shopify_product_handle, shopify_options);
  const productUrlSearch = `?${customAttributesToAttributionQueryParams(lineItemCustomAttributes)}`;

  return (
    <div className={styles.card}>
      <div className="flex gap-3 sm:gap-1 flex-row-reverse sm:flex-col">
        <div className="flex flex-col gap-1">
          <Link
            to={{
              pathname: productUrl,
              search: productUrlSearch,
            }}
            className="leading-normal font-bold hover:underline hover:text-black"
          >
            {title}
          </Link>

          <div className="flex flex-col">
            {on_sale ? (
              <div className={styles.sale}>
                <span className={styles.price}>{toDollars(price)}</span>
                <s className={styles.regPrice}>{toDollars(compare_at_price)}</s>
              </div>
            ) : (
              <span className={styles.price}>{toDollars(price)}</span>
            )}
          </div>
        </div>

        <Link
          to={{
            pathname: productUrl,
            search: productUrlSearch,
          }}
        >
          <div className={styles.img}>
            <img src={setShopifyImgWidth(img_url, 200)} width={140} height={140} />
            <span className={styles.season}>
              <SeasonIcon seasonName={season.name} className={styles.seasonIcon} tag={'span'} />
              {season.display_name}
            </span>
            {trending && <span className={styles.trending}>Trending</span>}
          </div>
        </Link>
      </div>

      <div className={styles.buttons}>
        <OmniaButton
          kind="tertiary"
          size="md"
          onPress={() => {
            WebAnalytics.mapInteracted({
              point: {
                lat: fishingReport.waterbody.lat,
                lon: fishingReport.waterbody.lng,
              },
              type: 'fishing_report',
              type_id: fishingReport.id.toString(),
            });
            replaceQueryString<ExtendedMapQueryParams>({
              [GlobalQueryParams.report_id]: fishingReport.id.toString(),
            });
          }}
        >
          View Fishing Report
        </OmniaButton>
        <OmniaButton
          kind="primary"
          size="md"
          isDisabled={!in_stock}
          onPress={() => {
            addToCart({
              productsToAdd: [
                {
                  product: featured_product,
                  quantity: 1,
                },
              ],
              addToCartArea: WebAnalyticsEventAreas.MAIN_VIEW,
              customAttributes: [
                {
                  key: LINE_ATTRIBUTION.FISHING_REPORT_ID,
                  value: fishingReport.id.toString(),
                },
              ],
              position,
              list_uuid: listUuid,
            });
          }}
        >
          <ShoppingCartOutlined /> {isMobile && 'Add to Cart'}
        </OmniaButton>
      </div>
    </div>
  );
};
